import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  MdArrowBack,
  MdBusiness,
  MdCheck,
  MdEdit,
  MdPeople,
  MdSchool,
  MdSearch,
} from "react-icons/md";
import { centerService } from "services/admin/centerService";
import { classMgmtService } from "services/admin/classMgmtService";
import { userService } from "services/admin/userService";
import type {
  Center,
  ClassListItem,
  UserListItem,
  UserRole,
} from "types/domain";
import ActionButton from "components/ui/ActionButton";
import StatusBadge from "components/ui/StatusBadge";
import { useLanguage } from "context/LanguageContext";

type BadgeTone =
  | "info"
  | "success"
  | "warning"
  | "scheduled"
  | "danger"
  | "neutral";

const STAFF_ROLES: UserRole[] = ["MANAGER", "TEACHER", "TRAINER", "STUDENT"];

const ROLE_LABEL_KEY: Record<UserRole, string> = {
  SYSTEM_ADMIN: "enum.role.SYSTEM_ADMIN",
  MANAGER: "enum.role.MANAGER",
  TEACHER: "enum.role.TEACHER",
  TRAINER: "enum.role.TRAINER",
  STUDENT: "enum.role.STUDENT",
};

// TEACHER maps to "scheduled" (teal) and MANAGER to "info" (brand) as the closest
// available tones since the shared StatusBadge kit has no purple/blue variants.
const ROLE_BADGE_TONE: Record<UserRole, BadgeTone> = {
  SYSTEM_ADMIN: "neutral",
  MANAGER: "info",
  TEACHER: "scheduled",
  TRAINER: "success",
  STUDENT: "warning",
};

const CLASS_STATUS_LABEL_KEY: Record<string, string> = {
  DRAFT: "centers.classStatus.DRAFT",
  ACTIVE: "centers.classStatus.ACTIVE",
  INACTIVE: "centers.classStatus.INACTIVE",
  COMPLETED: "centers.classStatus.COMPLETED",
  CANCELLED: "centers.classStatus.CANCELLED",
};

// COMPLETED maps to "info" as the closest available tone (no dedicated "done" tone).
const CLASS_STATUS_TONE: Record<string, BadgeTone> = {
  DRAFT: "neutral",
  ACTIVE: "success",
  INACTIVE: "warning",
  COMPLETED: "info",
  CANCELLED: "danger",
};

interface UserAvatarProps {
  user: UserListItem;
}

function UserAvatar({ user }: UserAvatarProps) {
  if (user.avatar_url) {
    return (
      <img
        src={user.avatar_url}
        alt={user.full_name}
        className="h-10 w-10 rounded-full object-cover"
      />
    );
  }

  const initials = user.full_name
    .split(" ")
    .map((n) => n[0])
    .slice(-2)
    .join("")
    .toUpperCase();

  return (
    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-100 text-base font-bold text-brand-600 dark:bg-brand-900/30 dark:text-brand-300">
      {initials}
    </div>
  );
}

function StaffCard({ user }: { user: UserListItem }) {
  const { t } = useLanguage();
  const roleKey = ROLE_LABEL_KEY[user.role];
  return (
    <div className="flex items-center justify-between gap-3 rounded-2xl border border-line bg-surface px-3 py-2.5">
      <div className="flex min-w-0 items-center gap-3">
        <UserAvatar user={user} />
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-ink">
            {user.full_name}
          </p>
          <p className="truncate text-xs text-ink-muted">
            {user.email}
          </p>
        </div>
      </div>
      <StatusBadge
        label={roleKey ? t(roleKey) : user.role}
        tone={ROLE_BADGE_TONE[user.role]}
        className="flex-shrink-0"
      />
    </div>
  );
}

function StaffColumn({
  title,
  users,
}: {
  title: string;
  users: UserListItem[];
}) {
  const { t } = useLanguage();
  return (
    <div className="rounded-2xl border border-line bg-gray-50/70 p-3 dark:bg-surface-muted/20">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h4 className="text-sm font-semibold text-ink">
          {title}
        </h4>
        <span className="rounded-full bg-surface px-2 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-300">
          {users.length}
        </span>
      </div>

      {users.length === 0 ? (
        <p className="py-6 text-center text-xs text-ink-subtle">
          {t("state.noData")}
        </p>
      ) : (
        <div className="space-y-2">
          {users.map((user) => (
            <StaffCard key={user.id} user={user} />
          ))}
        </div>
      )}
    </div>
  );
}

function CourseCard({ course }: { course: ClassListItem }) {
  const { t, locale } = useLanguage();
  const statusKey = CLASS_STATUS_LABEL_KEY[course.status];
  return (
    <div className="rounded-2xl border border-line bg-surface p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h4 className="text-base font-semibold text-ink">
            {course.name}
          </h4>
          <p className="mt-1 text-sm text-ink-muted">
            {course.description || t("centers.detail.noDescription")}
          </p>
        </div>
        <StatusBadge
          label={statusKey ? t(statusKey) : course.status}
          tone={CLASS_STATUS_TONE[course.status] ?? "neutral"}
        />
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-ink-muted">
        <span>
          {t("centers.detail.classCode", { code: course.class_code || "—" })}
        </span>
        <span>•</span>
        <span>
          {t("centers.createdOn", {
            date: new Date(course.created_at).toLocaleDateString(locale),
          })}
        </span>
      </div>
    </div>
  );
}

export default function CenterDetail() {
  const { centerId } = useParams<{ centerId: string }>();
  const navigate = useNavigate();
  const { t, locale } = useLanguage();

  const [center, setCenter] = React.useState<Center | null>(null);
  const [loadingCenter, setLoadingCenter] = React.useState(true);
  const [centerError, setCenterError] = React.useState<string | null>(null);

  const [editing, setEditing] = React.useState(false);
  const [form, setForm] = React.useState({ name: "", code: "", address: "" });
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = React.useState(false);

  const [staffUsers, setStaffUsers] = React.useState<UserListItem[]>([]);
  const [staffSearch, setStaffSearch] = React.useState("");
  const [loadingStaff, setLoadingStaff] = React.useState(true);
  const [staffError, setStaffError] = React.useState<string | null>(null);

  const [courses, setCourses] = React.useState<ClassListItem[]>([]);
  const [loadingCourses, setLoadingCourses] = React.useState(true);
  const [coursesError, setCoursesError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!centerId) return;

    setLoadingCenter(true);
    setCenterError(null);

    centerService
      .getById(centerId)
      .then((c) => {
        setCenter(c);
        setForm({ name: c.name, code: c.code || "", address: c.address || "" });
      })
      .catch(() => setCenterError("centers.detail.loadError"))
      .finally(() => setLoadingCenter(false));
  }, [centerId]);

  React.useEffect(() => {
    if (!centerId) return;

    const loadUsersByRole = async (role: UserRole): Promise<UserListItem[]> => {
      const pageSize = 200;
      let skip = 0;
      const merged: UserListItem[] = [];

      while (true) {
        const res = await userService.getAll({
          role,
          center_id: centerId,
          skip,
          limit: pageSize,
        });

        const matchingUsers = res.items.filter(
          (user) => user.center_id === centerId
        );
        merged.push(...matchingUsers);

        if (res.items.length < pageSize) {
          break;
        }
        skip += pageSize;
      }

      return merged;
    };

    const loadStaffUsers = async () => {
      setLoadingStaff(true);
      setStaffError(null);
      try {
        const lists = await Promise.all(STAFF_ROLES.map(loadUsersByRole));
        const dedup = new Map<string, UserListItem>();
        lists.flat().forEach((user) => dedup.set(user.id, user));
        setStaffUsers(Array.from(dedup.values()));
      } catch {
        setStaffUsers([]);
        setStaffError("centers.detail.staffLoadError");
      } finally {
        setLoadingStaff(false);
      }
    };

    loadStaffUsers();
  }, [centerId]);

  React.useEffect(() => {
    if (!centerId) return;

    const loadCourses = async () => {
      setLoadingCourses(true);
      setCoursesError(null);

      try {
        const pageSize = 200;
        let skip = 0;
        let allCourses: ClassListItem[] = [];

        while (true) {
          const res = await classMgmtService.getAll({
            center_id: centerId,
            skip,
            limit: pageSize,
          });
          allCourses = [...allCourses, ...res.items];

          if (res.items.length < pageSize) {
            break;
          }
          skip += pageSize;
        }

        setCourses(allCourses);
      } catch {
        setCourses([]);
        setCoursesError("centers.detail.classesLoadError");
      } finally {
        setLoadingCourses(false);
      }
    };

    loadCourses();
  }, [centerId]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!centerId) return;

    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      const updated = await centerService.update(centerId, {
        name: form.name,
        code: form.code || undefined,
        address: form.address || undefined,
      });
      setCenter(updated);
      setSaveSuccess(true);
      setEditing(false);
    } catch {
      setSaveError("centers.detail.updateError");
    } finally {
      setSaving(false);
    }
  };

  const filteredStaffUsers = React.useMemo(() => {
    const keyword = staffSearch.trim().toLowerCase();
    if (!keyword) return staffUsers;

    return staffUsers.filter((user) => {
      const roleKey = ROLE_LABEL_KEY[user.role];
      const roleLabel = (roleKey ? t(roleKey) : user.role).toLowerCase();
      return (
        user.full_name.toLowerCase().includes(keyword) ||
        user.email.toLowerCase().includes(keyword) ||
        roleLabel.includes(keyword)
      );
    });
  }, [staffSearch, staffUsers, t]);

  const managerUsers = React.useMemo(
    () => filteredStaffUsers.filter((user) => user.role === "MANAGER"),
    [filteredStaffUsers]
  );

  const teacherUsers = React.useMemo(
    () => filteredStaffUsers.filter((user) => user.role === "TEACHER"),
    [filteredStaffUsers]
  );

  const trainerUsers = React.useMemo(
    () => filteredStaffUsers.filter((user) => user.role === "TRAINER"),
    [filteredStaffUsers]
  );

  const studentUsers = React.useMemo(
    () => filteredStaffUsers.filter((user) => user.role === "STUDENT"),
    [filteredStaffUsers]
  );

  const sortedCourses = React.useMemo(
    () =>
      [...courses].sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      ),
    [courses]
  );

  const inputClass =
    "w-full rounded-xl border border-line bg-surface px-4 py-2.5 text-sm text-ink outline-none transition-colors focus:border-brand-500 focus:ring-1 focus:ring-brand-500";

  if (loadingCenter) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="border-t-transparent h-8 w-8 animate-spin rounded-full border-4 border-brand-500" />
      </div>
    );
  }

  if (centerError || !center) {
    return (
      <div className="mt-5 px-4">
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-900/10 dark:text-red-400">
          {t(centerError ?? "centers.detail.notFound")}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-5 space-y-5 px-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <ActionButton
          variant="ghost"
          size="sm"
          onClick={() => navigate("/systemadmin/centers")}
          startIcon={<MdArrowBack className="h-4 w-4" />}
          className="text-ink-muted hover:text-navy-700"
        >
          {t("centers.detail.backToList")}
        </ActionButton>
        {!editing && (
          <ActionButton
            variant="secondary"
            onClick={() => {
              setEditing(true);
              setSaveSuccess(false);
            }}
            startIcon={<MdEdit className="h-4 w-4" />}
          >
            {t("action.edit")}
          </ActionButton>
        )}
      </div>

      <div className="rounded-2xl border border-line bg-surface p-6 shadow-sm">
        <div className="mb-5 flex items-start gap-3">
          <div className="rounded-xl bg-brand-50 p-2.5 text-brand-500 dark:bg-brand-500/10">
            <MdBusiness className="h-6 w-6" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-ink">
              {center.name}
            </h2>
            {center.code && (
              <p className="mt-0.5 text-xs font-medium text-brand-500">
                {t("centers.detail.code", { code: center.code })}
              </p>
            )}
            <p className="mt-0.5 text-sm text-ink-muted">
              {center.address || t("centers.noAddress")}
            </p>
            <p className="mt-1 text-xs text-ink-subtle">
              {t("centers.detail.createdAt", {
                date: new Date(center.created_at).toLocaleDateString(locale),
              })}
            </p>
          </div>
        </div>

        {editing ? (
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-ink">
                {t("centers.field.name")}{" "}
                <span className="text-red-500">*</span>
              </label>
              <input
                className={inputClass}
                value={form.name}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, name: e.target.value }))
                }
                required
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-ink">
                {t("centers.field.code")}
              </label>
              <input
                className={inputClass}
                value={form.code}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, code: e.target.value }))
                }
                placeholder={t("centers.field.codePlaceholder")}
                maxLength={20}
              />
              <p className="mt-1 text-xs text-ink-subtle">
                {t("centers.field.codeHint")}
              </p>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-ink">
                {t("centers.field.address")}
              </label>
              <input
                className={inputClass}
                value={form.address}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, address: e.target.value }))
                }
                placeholder={t("centers.field.addressInput")}
              />
            </div>

            {saveError && (
              <div className="rounded-lg bg-red-50 px-4 py-2.5 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-400">
                {t(saveError)}
              </div>
            )}

            <div className="flex gap-3 pt-1">
              <ActionButton
                type="submit"
                variant="primary"
                disabled={saving}
                startIcon={<MdCheck className="h-4 w-4" />}
              >
                {saving ? t("action.saving") : t("settings.save")}
              </ActionButton>
              <ActionButton
                type="button"
                variant="secondary"
                disabled={saving}
                onClick={() => {
                  setEditing(false);
                  setForm({
                    name: center.name,
                    code: center.code || "",
                    address: center.address || "",
                  });
                  setSaveError(null);
                }}
              >
                {t("action.cancel")}
              </ActionButton>
            </div>
          </form>
        ) : saveSuccess ? (
          <div className="rounded-lg bg-green-50 px-4 py-2.5 text-sm text-green-700 dark:bg-green-900/20 dark:text-green-400">
            {t("centers.detail.updateSuccess")}
          </div>
        ) : null}
      </div>

      <div className="rounded-2xl border border-line bg-surface p-6 shadow-sm">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <MdPeople className="h-5 w-5 text-brand-500" />
            <h3 className="text-base font-semibold text-ink">
              {t("centers.detail.staffAndStudents")}
            </h3>
            <span className="rounded-full bg-surface-muted px-2 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-300">
              {filteredStaffUsers.length}
            </span>
          </div>

          <div className="relative w-full max-w-md">
            <MdSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-subtle" />
            <input
              value={staffSearch}
              onChange={(e) => setStaffSearch(e.target.value)}
              placeholder={t("centers.detail.staffSearchPlaceholder")}
              className="w-full rounded-xl border border-line bg-surface py-2 pl-9 pr-3 text-sm text-ink outline-none focus:border-brand-500"
            />
          </div>
        </div>

        {loadingStaff ? (
          <p className="py-5 text-sm text-ink-muted">
            {t("centers.detail.staffLoading")}
          </p>
        ) : staffError ? (
          <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-400">
            {t(staffError)}
          </div>
        ) : filteredStaffUsers.length === 0 ? (
          <p className="py-5 text-center text-sm text-ink-subtle">
            {t("centers.detail.staffEmpty")}
          </p>
        ) : (
          <>
            {managerUsers.length > 0 && (
              <div className="mb-3">
                <StaffColumn
                  title={t("enum.role.MANAGER")}
                  users={managerUsers}
                />
              </div>
            )}
            <div className="grid grid-cols-1 gap-3 xl:grid-cols-3">
              <StaffColumn
                title={t("enum.role.TEACHER")}
                users={teacherUsers}
              />
              <StaffColumn
                title={t("enum.role.TRAINER")}
                users={trainerUsers}
              />
              <StaffColumn
                title={t("enum.role.STUDENT")}
                users={studentUsers}
              />
            </div>
          </>
        )}
      </div>

      <div className="rounded-2xl border border-line bg-surface p-6 shadow-sm">
        <div className="mb-4 flex items-center gap-2">
          <MdSchool className="h-5 w-5 text-brand-500" />
          <h3 className="text-base font-semibold text-ink">
            {t("centers.detail.classesTitle")}
          </h3>
          <span className="rounded-full bg-surface-muted px-2 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-300">
            {sortedCourses.length}
          </span>
        </div>

        {loadingCourses ? (
          <p className="py-5 text-sm text-ink-muted">
            {t("centers.detail.classesLoading")}
          </p>
        ) : coursesError ? (
          <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-400">
            {t(coursesError)}
          </div>
        ) : sortedCourses.length === 0 ? (
          <p className="py-5 text-center text-sm text-ink-subtle">
            {t("centers.detail.classesEmpty")}
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
            {sortedCourses.map((course) => (
              <CourseCard key={course.id} course={course} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
